/**
 * LaneV2TaskPanel — the whole task, composed from a single `TaskSnapshot` (R3).
 *
 * COMPOSES, DOES NOT RE-DERIVE. Every section below owns its own rendering of its own slice of the
 * snapshot; this only decides ORDER (the journey: stepper → plan → build → package → validation) and
 * PRESENCE — a section renders only when its data exists, so a task that has not reached building yet
 * shows a stepper and a plan and nothing else, rather than empty build/package/validation cards
 * implying those phases ran and produced nothing.
 *
 * Cancel is offered only on a task that could still be cancelled (not terminal, not mid-apply — `applying` is
 * deliberately excluded because a cancel against a write in flight is a different, riskier action — and not at rest in
 * `validated` (a forward task; a build-less revert waiting on approve keeps Cancel), where the change is done and "Cancel task" would only mislead) and only when the caller supplied a
 * handler. It lives in the "⋯" overflow, not the toolbar. Revert is a legacy affordance: offered only for an `applied`
 * row (forward tasks never reach `applied`), also in the overflow.
 *
 * ORDER (once the changeset is proposed): Package → Validation → Plan (collapsed). The panel is where the changes are
 * reviewed, so the changes come first; before that the plan is the news and leads, as before.
 *
 * STATUS ONLY for the package: Save / Validate belong to the chat's outcome card (`LaneV2TaskOutcome` →
 * `PostBuildActions`), the one owner, so the panel carries no second copy with its own state.
 */
import { useState } from "react";
import { Loader2, RotateCcw, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { TaskSnapshot } from "@/services/multi-agent/laneV2/types";
import { BuildProgressView } from "./BuildProgressView";
import { EtaStrip } from "./EtaStrip";
import { isRevertShaped } from "./changeTarget";
import { PackageView } from "./PackageView";
import { PlanRevisionView } from "./PlanRevisionView";
import { BuildSummary, PhaseTracker } from "./PhaseTracker";
import { restingCheckFailed, restingVerdictOf } from "@/services/multi-agent/laneV2/presentation";
import { TaskStateStepper } from "./TaskStateStepper";
import { ValidationView } from "./ValidationView";
import { TRACKER_MAX_ITEMS } from "./trackerModel";
import { buildItemsByKey, isTerminalState } from "./views";

/** States from which the changes lead the panel and the plan folds away. */
const REVIEW_STATES: ReadonlySet<string> = new Set(["changeset_proposed", "checking", "validated", "applying", "applied"]);

const OVERFLOW_ITEM =
  "inline-flex items-center gap-1.5 rounded px-2 py-1 text-left text-[13px] font-medium text-muted-foreground hover:bg-muted/50 hover:text-foreground disabled:opacity-50";

export interface LaneV2TaskPanelProps {
  snapshot: TaskSnapshot;
  onViewItemBody?: (workItemId: string) => void;
  onCancelTask?: () => void;
  onRevert?: () => void;
  busy?: boolean;
  /** Start with the build details open (the node-rendered tests cannot click the toggle). */
  initialDetailsOpen?: boolean;
  className?: string;
}

export function LaneV2TaskPanel({
  snapshot,
  onViewItemBody,
  onCancelTask,
  onRevert,
  busy,
  initialDetailsOpen = false,
  className,
}: LaneV2TaskPanelProps) {
  const [detailsOpen, setDetailsOpen] = useState(initialDetailsOpen);
  // One affordance per act: when the server offers `discard`, the outcome card owns it and the panel offers no Cancel either.
  const discardOffered = (snapshot.actions ?? []).includes("discard");
  // A running branch save (R3, the server's fact) hides Cancel too: the server drops `discard` from actions meanwhile and refuses the cancel.
  const saveRunning = snapshot.package?.branch_save_running != null;
  const canCancel = !discardOffered && !saveRunning && onCancelTask && !isTerminalState(snapshot.state) && snapshot.state !== "applying" && !(snapshot.state === "validated" && !isRevertShaped(snapshot));
  const canRevert = onRevert && snapshot.state === "applied";
  const compactTracker = !!snapshot.plan && snapshot.plan.items.length <= TRACKER_MAX_ITEMS;
  const hasOverview = !!snapshot.plan;
  const reviewing = REVIEW_STATES.has(snapshot.state);
  const itemsByKey = snapshot.build ? buildItemsByKey(snapshot.build.items) : undefined;

  const changesSection = (
    <>
      {snapshot.package && <PackageView pkg={snapshot.package} itemsByKey={itemsByKey} onViewItemBody={onViewItemBody} />}
      {snapshot.validation && <ValidationView validation={snapshot.validation} />}
    </>
  );

  return (
    <div data-lane-v2-task-panel={snapshot.task_id} className={cn("space-y-3", className)}>
      {(canCancel || canRevert) && (
        <div className="flex justify-end">
          <details data-lane-v2-panel-overflow="" className="relative">
            <summary
              aria-label="More actions"
              className="flex h-7 w-7 cursor-pointer list-none items-center justify-center rounded-md border border-border text-[15px] leading-none text-muted-foreground hover:bg-muted/50 hover:text-foreground [&::-webkit-details-marker]:hidden"
            >
              <span aria-hidden>⋯</span>
            </summary>
            <div className="absolute right-0 z-10 mt-1 flex min-w-[9rem] flex-col gap-1 rounded-md border border-border bg-card p-1 shadow-md">
              {canCancel && (
                <button type="button" disabled={busy} onClick={onCancelTask} className={OVERFLOW_ITEM}>
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <XCircle className="h-3.5 w-3.5" aria-hidden />}
                  Cancel task
                </button>
              )}
              {canRevert && (
                <button type="button" disabled={busy} onClick={onRevert} className={OVERFLOW_ITEM}>
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <RotateCcw className="h-3.5 w-3.5" aria-hidden />}
                  Revert
                </button>
              )}
            </div>
          </details>
        </div>
      )}

      {/* One phase row: the compact tracker has its own, so the stepper only shows without it. */}
      {!compactTracker && <TaskStateStepper state={snapshot.state} transitions={snapshot.transitions} lastCheckFailed={restingCheckFailed(snapshot)} verdict={restingVerdictOf(snapshot)} />}

      <EtaStrip snapshot={snapshot} />

      {reviewing && changesSection}

      {snapshot.plan && <PlanRevisionView plan={snapshot.plan} collapsed={reviewing} />}

      {snapshot.plan &&
        (snapshot.plan.items.length <= TRACKER_MAX_ITEMS ? (
          <PhaseTracker snapshot={snapshot} />
        ) : (
          <BuildSummary snapshot={snapshot} onViewItemBody={onViewItemBody} />
        ))}

      {snapshot.build && hasOverview && (
        <div>
          <button
            type="button"
            aria-expanded={detailsOpen}
            onClick={() => setDetailsOpen((v) => !v)}
            data-lane-v2-build-details-toggle=""
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[12px] font-medium text-muted-foreground hover:bg-muted/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span aria-hidden>{detailsOpen ? "▾" : "▸"}</span>
            Details
          </button>
          {detailsOpen && <BuildProgressView build={snapshot.build} onViewItemBody={onViewItemBody} className="mt-2" />}
        </div>
      )}
      {snapshot.build && !hasOverview && <BuildProgressView build={snapshot.build} onViewItemBody={onViewItemBody} />}

      {!reviewing && changesSection}
    </div>
  );
}
